import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { BusinessData } from '@app/modules/model/business';

import { TaxIdValidators } from '@app/app.commonutils';
import { geocodeService } from '@app/modules/common/geocode';
import { languageSupport, languageService } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';

/********************************/
/* COMPONENT STARTS HERE        */
/********************************/

export interface UiBusinessEditChange {
    formValid: boolean;
    invoiceProceedable: boolean;
    taxIdUnverified: boolean;
}

@Component({
    selector: 'ui-business-edit',
    templateUrl:'./ui-edit.html',
    styleUrls: [ 
        './ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiBusinessEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('business') _business: BusinessData = null;
    @Input('accounts') _listaccount: boolean = true;
    @Input('readonly') _readonly: boolean = false;

    private _accounts = null;

    private get Accounts(){
        if (this._accounts == null){
            let _accounts = [];
            for(let _account of this._place.payaccounts){
                if (_account.client && _account.IsValid) {
                    _accounts.push(_account.client);
                }
            }
            this._accounts = _accounts;
        }

        return this._accounts;
    }

    IsPayAccount(business){
        return this.Accounts.includes(business);
    }

    get Clients(){
        let _clients = [];
        for (let _business of this._place.clients){
            if ((_business != this._business) && (_business.IsValid) && (this._listaccount || !this.IsPayAccount(_business))){
                _clients.push(_business);
            }
        }
        return _clients;
    }

    private _businessForm = null;
    get BusinessForm(){
        if (this._businessForm == null){
            this.LoadBusinessForm();
        }
        return this._businessForm;
    }

    private _ValidateTaxId(control: FormControl){
        let _taxid = TaxIdValidators.ToTaxId(control.value);
        if (_taxid){
            if (!TaxIdValidators.ValidateTaxId(_taxid)){
                return { invalid: true };    
            }
        }

        return null;
    }

    private _ready = false;
    private LoadBusinessForm(){
        let _name = this._business.name;
        let _taxid = this._business.taxid;
        let _email = this._business.email;
        let _address = this._business.address;
        let _postal = this._business.postalcode;
        let _region = this._business.region;

        if (this._businessForm == null){
            this._businessForm = new FormGroup({
                merchantName: new FormControl(_name, [
                    Validators.required 
                ]),
                merchantEmail: new FormControl(_email, [
                    Validators.email 
                ]),
                merchantAddress: new FormControl(_address, [
                    // no validators
                ]),
                merchantPostal: new FormControl(_postal, [
                    // no validators
                ]),
                merchantRegion: new FormControl(_region, [
                    // no validators
                ]),
                merchantTaxid: new FormControl(_taxid, [ 
                    Validators.required,
                    this._ValidateTaxId.bind(this)
                ]),
            });
        }
        else {
            this._businessForm.patchValue({
                merchantName: _name,
                merchantEmail: _email,
                merchantAddress: _address,
                merchantPostal: _postal,
                merchantRegion: _region,
                merchantTaxid: _taxid
            });
        }

        this._ready = false;
        this._businessForm.markAsPristine();
        setTimeout(() => {
            this._businessForm.markAsPristine();
            this._ready = true;
        }, 0);

        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _place_subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private geocode: geocodeService, public view: viewService){
        super(lang, change);
    }

    ngOnInit(){
        // nothing to do
    }

    @Output('onChange') _onChange = new EventEmitter<UiBusinessEditChange>();

    private _syncBusinessFromForm(){
        this._business.name = this.BusinessForm.get('merchantName').value;
        this._business.taxid = TaxIdValidators.ToTaxId(this.BusinessForm.get('merchantTaxid').value);
        this._business.email = this.BusinessForm.get('merchantEmail').value;
        this._business.address = this.BusinessForm.get('merchantAddress').value;
        this._business.postalcode = this.BusinessForm.get('merchantPostal').value;
        this._business.region = this.BusinessForm.get('merchantRegion').value;
    }

    /** True when only the Spanish DNI/CIF algorithm fails (foreign IDs, typos). */
    private _taxIdUnverified(): boolean {
        let _taxid = TaxIdValidators.ToTaxId(this.BusinessForm.get('merchantTaxid').value);
        return !!(_taxid && !TaxIdValidators.ValidateTaxId(_taxid));
    }

    /** Allow invoice when everything but the strict tax-id checksum is satisfied. */
    private _invoiceProceedable(): boolean {
        let f = this.BusinessForm;
        let taxErr = f.get('merchantTaxid').errors;
        if (f.get('merchantName').errors){
            return false;
        }
        if (taxErr && taxErr['required']){
            return false;
        }
        if (f.get('merchantEmail').errors){
            return false;
        }
        if (f.get('merchantPostal').errors){
            return false;
        }
        if (f.get('merchantRegion').errors){
            return false;
        }
        return true;
    }

    private onChange() {
        this._syncBusinessFromForm();

        this._onChange.emit({
            formValid: this.BusinessForm.valid,
            invoiceProceedable: this._invoiceProceedable(),
            taxIdUnverified: this._taxIdUnverified()
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        this._place_subscription = this._place.OnTargetRefresh('CLIENT').subscribe(
        data => {
            this._accounts = null;
            this.change.markForCheck();
        });    
        
        this.onChange();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }        
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    OnTaxIdChanged(){
        let _taxid = TaxIdValidators.ToTaxId(this._businessForm.get('merchantTaxid').value);
        if (_taxid != this._businessForm.get('merchantTaxid').value){
            this._businessForm.get('merchantTaxid').setValue(_taxid);
            this.change.markForCheck();
        }
    }

    OnValueChanged(){
        this.onChange();
    }

    private _businessfield = null;
    private _businessoptions = null;

    Autocomplete(field){
        if (!this._ready || (this._businessfield != field) || (this.BusinessOptions.length < 1)){
            return false;   // cannot complete
        }

        if ((this.BusinessOptions.length == 1) && this.BusinessForm.valid){
            return false;   // already completed
        }

        return true;
    }

    AutocompleteOptions(field){
        this._businessoptions = null;
        this._businessfield = field;
        this.OnValueChanged();
    }

    get BusinessOptions(){
        if (!this._businessoptions){
            let _options = [];

            let _name = (this.BusinessForm.get('merchantName').value)?.trim().toLowerCase();
            let _taxid = (this.BusinessForm.get('merchantTaxid').value)?.trim().toLowerCase();

            for (let _business of this.Clients){
                let _includes = (_name && _name != '') || (_taxid && _taxid != '');;

                if (_includes && _name && !_business.name.toLowerCase().includes(_name)){
                    _includes = false;
                }

                if (_includes && _taxid && !_business.taxid.toLowerCase().includes(_taxid)){
                    _includes = false;
                }

                if (_includes && (_options.length < 10)){
                    _options.push(_business);
                }
            }

            this._businessoptions = _options;
        }

        return this._businessoptions;
    }

    OnSelect(business){
        this._businessForm.patchValue({
            merchantName: business.name,
            merchantEmail: business.email,
            merchantAddress: business.address,
            merchantPostal: business.postalcode,
            merchantRegion: business.region,
            merchantTaxid: business.taxid
        });

        this.OnValueChanged();
    }

    private _lastaddress = null;
    async OnAddressChanged(){
        let _formatted = this._businessForm.get('merchantAddress').value;
        if (_formatted == this._lastaddress){
            return; // focus is lost, but no change in address
        }

        this._businessForm.get('merchantPostal').setValue(null);
        this._businessForm.get('merchantRegion').setValue(null);    

        let _controls = [
            this._businessForm.get('merchantPostal'),
            this._businessForm.get('merchantRegion')
        ];

        if (_formatted && _formatted.length != 0){
            for(let _control of _controls){
                _control.setValidators([ Validators.required ]);
                _control.setErrors({ required: true });
                _control.updateValueAndValidity();    
            }

            let _address = await this.geocode.ResolveAddress(_formatted);
            if (_address){
                this._businessForm.get('merchantPostal').setValue(_address['postal_code']);
                this._businessForm.get('merchantRegion').setValue(_address['area']['level2'] || _address['area']['level1']);
            }
            else {
                this._businessForm.get('merchantPostal').setValue('');
                this._businessForm.get('merchantRegion').setValue('');    
            }
        }
        else {
            for(let _control of _controls){
                _control.clearValidators();
                _control.setErrors(null);
                _control.updateValueAndValidity();    
            }

            this._businessForm.get('merchantPostal').setValue('');
            this._businessForm.get('merchantRegion').setValue('');
        }

        this.OnValueChanged();
        this.change.markForCheck();
    }
}
